import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navber from "./Navber";
import { motion, AnimatePresence } from "framer-motion";

export default function Layout() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Persist sidebar state in localStorage
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Auto collapse sidebar on mobile
      if (mobile) {
        setIsCollapsed(true);
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Page transition variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobile && isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Responsive positioning */}
      <div
        className={`
          h-full transition-all duration-300 ease-in-out fixed z-50
          ${isMobile 
            ? isMobileMenuOpen 
              ? "left-0" 
              : "-left-72" 
            : isCollapsed 
              ? "w-20" 
              : "w-72"
          }
        `}
      >
        <Sidebar 
          isCollapsed={isCollapsed} 
          setIsCollapsed={setIsCollapsed}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
        />
      </div>

      {/* Mobile Menu Toggle Button */}
      {isMobile && !isMobileMenuOpen && (
        <motion.button
          initial={{ scale: 0, x: -20 }}
          animate={{ scale: 1, x: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleMobileMenu}
          className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full shadow-lg shadow-cyan-500/30 flex items-center justify-center text-white lg:hidden border border-cyan-400/50"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 6h16M4 12h16M4 18h16" 
            />
          </svg>
        </motion.button>
      )}

      {/* Main Content Area */}
      <div
        className={`
          flex flex-col h-full transition-all duration-300 ease-in-out flex-1
          ${!isMobile 
            ? isCollapsed 
              ? "ml-20" 
              : "ml-72"
            : "ml-0"
          }
        `}
      >
        {/* Navbar */}
        <div className="flex-shrink-0 sticky top-0 z-30">
          <Navber 
            isMobile={isMobile}
            toggleMobileMenu={toggleMobileMenu}
          />
        </div>

        {/* Main Content Container */}
        <main className="flex-1 overflow-hidden relative">
          {/* Gradient Background with custom scrollbar */}
          <div className="h-full overflow-y-auto p-3 md:p-6 bg-gradient-to-br from-gray-900/50 via-gray-900/50 to-gray-950/50 scrollbar-custom">
            
            {/* Animated Page Indicator */}
            <div className="mb-4 px-2">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 text-xs text-gray-400"
              >
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></span>
                <span className="capitalize">
                  {location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'Dashboard'}
                </span>
              </motion.div>
            </div>
            
            {/* Glassmorphism Content Container */}
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="bg-gradient-to-br from-gray-800/40 via-gray-800/30 to-gray-900/40 backdrop-blur-md rounded-2xl border border-gray-700/50 shadow-2xl shadow-cyan-500/5 overflow-hidden h-[calc(100%-3rem)] hover:border-cyan-500/20 transition-colors duration-300"
            >
              <div className="p-4 md:p-6 h-full overflow-y-auto scrollbar-transparent relative">
                
                {/* Decorative gradient elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
                
                {/* Content */}
                <div className="relative z-10">
                  <Outlet />
                </div>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Footer - Optional */}
        <footer className="flex-shrink-0 py-2 px-4 md:px-6 text-xs text-gray-500 border-t border-gray-800/50 bg-gray-900/30 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span>© 2026 E-Attendance System</span>
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
              <span>v2.0.0</span>
            </div>
          </div>
        </footer>
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        /* Custom scrollbar for the entire app */
        .scrollbar-custom::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        .scrollbar-custom::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .scrollbar-custom::-webkit-scrollbar-thumb {
          background: rgba(6, 182, 212, 0.3);
          border-radius: 20px;
          transition: all 0.3s ease;
        }
        
        .scrollbar-custom::-webkit-scrollbar-thumb:hover {
          background: rgba(6, 182, 212, 0.5);
        }
        
        .scrollbar-custom {
          scrollbar-width: thin;
          scrollbar-color: rgba(6, 182, 212, 0.3) transparent;
        }
        
        /* Transparent scrollbar for content area */
        .scrollbar-transparent::-webkit-scrollbar {
          width: 4px;
        }
        
        .scrollbar-transparent::-webkit-scrollbar-thumb {
          background: rgba(6, 182, 212, 0.2);
          border-radius: 20px;
        }
        
        .scrollbar-transparent::-webkit-scrollbar-thumb:hover {
          background: rgba(6, 182, 212, 0.4);
        }
        
        .scrollbar-transparent {
          scrollbar-width: thin;
          scrollbar-color: rgba(6, 182, 212, 0.2) transparent;
        }

        /* Loading animation */
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 3s ease infinite;
        }
      `}</style>
    </div>
  );
}